import { T, Var } from 'gt-react';

import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type {
  PivotCollision,
  PivotFileView,
  PivotSummary,
} from '@/lib/price-list-break-pivot/types';
import { SummaryStat } from '../SummaryStat';

function FileStatusBadge({ view }: { view: PivotFileView }) {
  if (view.status === 'pivoted') {
    return (
      <Badge variant="success">
        <T context="badge when a price list was pivoted">Pivoted</T>
      </Badge>
    );
  }
  if (view.status === 'needs-mapping') {
    return (
      <Badge variant="warning">
        <T context="badge when a price list still needs a break column">
          Needs mapping
        </T>
      </Badge>
    );
  }
  if (view.status === 'unreadable') {
    return (
      <Badge variant="destructive">
        <T context="badge when a price list file could not be parsed">
          Couldn’t read
        </T>
      </Badge>
    );
  }
  if (view.status === 'empty') {
    return (
      <Badge variant="secondary">
        <T context="badge when a dropped workbook has no header row">Empty</T>
      </Badge>
    );
  }
  return (
    <Badge variant="secondary">
      <T context="badge when a sheet is missing the chosen pivot columns">
        Unrecognized
      </T>
    </Badge>
  );
}

export function FileResultList({
  views,
  onRemove,
  isParsing,
}: {
  views: PivotFileView[];
  onRemove: (id: string) => void;
  isParsing: boolean;
}) {
  if (views.length === 0) return null;
  return (
    <ul className="flex flex-col gap-2">
      {views.map((view) => (
        <li
          key={view.id}
          className="flex items-center gap-2 rounded-lg border p-3"
        >
          <span className="min-w-0 flex-1 truncate text-sm font-medium">
            {view.fileName}
          </span>
          <FileStatusBadge view={view} />
          <button
            type="button"
            className="text-xs text-muted-foreground underline-offset-2 hover:underline disabled:no-underline disabled:opacity-50"
            disabled={isParsing}
            onClick={() => onRemove(view.id)}
          >
            <T context="button that removes one price list file from the list">
              Remove
            </T>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function PivotSummaryCards({ summary }: { summary: PivotSummary }) {
  if (summary.fileCount === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <SummaryStat
        label={<T context="count of price list files dropped">Files</T>}
        value={summary.fileCount}
      />
      <SummaryStat
        label={
          <T context="count of unique items after pivoting price breaks">
            Items
          </T>
        }
        value={summary.itemCount}
      />
      <SummaryStat
        label={
          <T context="count of distinct quantity-break price columns created">
            Break columns
          </T>
        }
        value={summary.breakColumnCount}
      />
      <SummaryStat
        label={
          <T context="count of items that had more than one quantity break">
            Multi-break items
          </T>
        }
        value={summary.multiBreakItemCount}
      />
    </div>
  );
}

const PREVIEW_LIMIT = 50;

export function CollisionsTable({
  collisions,
}: {
  collisions: PivotCollision[];
}) {
  if (collisions.length === 0) return null;
  const visible = collisions.slice(0, PREVIEW_LIMIT);
  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">
        <T context="heading for colliding key and break prices">Collisions</T>
      </h2>
      {collisions.length > PREVIEW_LIMIT ? (
        <p className="text-xs text-muted-foreground">
          <T context="note that the on-screen collision table is a preview">
            Showing <Var>{PREVIEW_LIMIT}</Var> of <Var>{collisions.length}</Var>{' '}
            collisions. Download the pivot for the last price kept.
          </T>
        </p>
      ) : null}
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <T context="collision table column for the source sheet">
                  Sheet
                </T>
              </TableHead>
              <TableHead>
                <T context="collision table column for the item key">Key</T>
              </TableHead>
              <TableHead>
                <T context="collision table column for the quantity break">
                  Break
                </T>
              </TableHead>
              <TableHead>
                <T context="collision table column for the previous price">
                  Previous
                </T>
              </TableHead>
              <TableHead>
                <T context="collision table column for the price that won">
                  Kept
                </T>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((row) => (
              <TableRow
                key={[
                  row.sheetName,
                  row.key,
                  row.breakValue,
                  row.previousPrice,
                  row.nextPrice,
                ].join('\u0001')}
              >
                <TableCell>{row.sheetName}</TableCell>
                <TableCell className="font-medium">{row.key}</TableCell>
                <TableCell>{row.breakValue}</TableCell>
                <TableCell>{row.previousPrice}</TableCell>
                <TableCell>{row.nextPrice}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
